import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { readTourStates, writeTourState } from "./tour-persistence";
import type { PersistedTourState, TourStates } from "./types";

export function useTourStates(userId: string | undefined) {
    return useQuery<TourStates>({
        queryKey: ["tour-state", userId],
        queryFn: () => readTourStates(userId!),
        enabled: !!userId,
        staleTime: Infinity,
    });
}

export function useSetTourState(userId: string | undefined) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ tourId, state }: { tourId: string; state: PersistedTourState }) => {
            if (!userId) return Promise.reject(new Error("No userId for tour persistence"));
            writeTourState(userId, tourId, state);
            return Promise.resolve({ tourId, state });
        },
        onSuccess: (_, { tourId, state }) => {
            queryClient.setQueryData<TourStates>(["tour-state", userId], (old) => ({
                ...old,
                [tourId]: state,
            }));
        },
        onError: (error, { tourId }) => {
            console.error("[Tours] Failed to persist tour state:", error);
            Sentry.captureException(error, { extra: { context: "tour-persistence-write", userId, tourId } });
            toast.warn("Tour progress could not be saved.", {
                autoClose: 5000,
            });
            // Defensive re-read from localStorage to ensure cache consistency after a write failure
            queryClient.invalidateQueries({ queryKey: ["tour-state", userId] });
        },
    });
}
